import { Component, OnInit, Renderer2, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IErrorDescription } from '@ibank-globals/error';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { finalize } from 'rxjs/operators/finalize';

import { BaseComponent } from '../../../../globals/classes/base.component';
import { ErrorHandler } from '../../../../globals/classes/errorHandler';
import { AppSettings } from '../../../app.settings';
import { SourceApis } from '../../../../globals/classes/errorDescription';

import { ICardLoginRequest } from '../../../classes/existing-onboarding-customer/requests';
import { ICardLoginResponse } from '../../../classes/existing-onboarding-customer/responses';
import { UserAuth } from '../../../classes/userAuth';
import { ErrorMessageModalComponent } from '../../error-message-modal/error-message-modal.component';
import { IInitializePasswordRequest } from '../../../classes/initialize-password';

const uuid = require('uuid/v4');

@Component({
    selector: 'card-verification',
    template: require('./card-verification.component.html'),
})
export class CardVerificationComponent extends BaseComponent implements OnInit {
    // Card-info properties
    acceptedCards: boolean = false;
    card: string = '';
    numbers: number[] = [0, 1, 2, 3];
    pins: number[] = [null, null, null, null];
    check: boolean[] = [false, false, false, false];

    // Card-details-modal properties
    public isLoading = false;
    public isValidCard: boolean;
    public isValidPin: boolean;
    public cardLoginRequest: ICardLoginRequest;
    public isNewErrorModal: boolean = AppSettings.isNewErrorModalEnabled;
    public isRebrading = AppSettings.isRebrading;

    // Input/Output properties
    @Input() public isForgotPasswordRetailModalFlow: boolean = true;
    @Input() initRequest: IInitializePasswordRequest;
    @Output() isValid = new EventEmitter<boolean>();
    @Output() cardVerified = new EventEmitter<ICardLoginResponse>();

    // Validation patterns
    private readonly validCardNumberRegex = /^\d{16}$/;
    private readonly validCardPINRegex = /^\d{4}$/;
    private readonly validOnlyNumberRegex = /^[0-9]+$/;

    private _invalidCredentials = new BehaviorSubject<boolean>(false);
    public readonly invalidCredentials$ = this._invalidCredentials.asObservable();

    constructor(
        private activeModal: NgbActiveModal,
        private modalService: NgbModal,
        private renderer: Renderer2,
        private elementRef: ElementRef
    ) {
        super();
    }

    public ngOnInit(): void {
        this.initRequests();

        this.mortalize(this.invalidCredentials$)
            .pipe(filter((value) => value === true))
            .subscribe(() => {
                this.pins = [null, null, null, null];
                const updatedPin = this.convertArrayToString(this.pins);
                this.cardLoginRequest.cardPIN = updatedPin;
                const isFormValid = this.isFormValid(this.card, updatedPin);
                this.isValidCard = isFormValid;
                this.isValidPin = isFormValid;
            });
    }

    public get canContinue(): boolean {
        return this.isValidCard && this.isValidPin;
    }

    // Methods from card-details-modal
    private initRequests(): void {
        this.cardLoginRequest = {
            applicationId: uuid(),
            cardNumber: '',
            cardPIN: '',
            userId: this.initRequest.userID
        };
    }

    public closeModal(): void {
        this.activeModal.close();
    }

    // Methods from card-info
    public toggleAcceptedCards() {
        this.acceptedCards = !this.acceptedCards;
    }

    public changeType(index: number): string {
        if (!this.check[index]) {
            return '';
        } else {
            return 'password';
        }
    }

    public fetchBackspaceKeyInputElem(event: KeyboardEvent, index: number) {
        if (event.code === 'Backspace' && index > 0 && this.pins[index] == null) {
            var prevPinInputElem = this.fetchHtmlPinInput(index - 1);
            if (!!prevPinInputElem) prevPinInputElem.focus();
        }
    }

    public showCardPinInputElem(event: KeyboardEvent, index: number) {
        setTimeout(() => {
            if (this.pins[index] !== null || this.pins[index] !== undefined) {
                this.check[index] = true;
            } else {
                this.check[index] = false;
            }
        }, 500);
    }

    public cardInputUpdated(cardNumber: string): void {
        this.cardLoginRequest.cardNumber = cardNumber;
        const isFormValid = this.isFormValid(cardNumber, this.convertArrayToString(this.pins));
        this.isValidCard = isFormValid;
        this.isValidPin = isFormValid;
        this.isValid.emit(isFormValid);
    }

    public cardInputFilled(event: KeyboardEvent) {
        const target = <HTMLInputElement>event.target;
        if (!this.isValidCardOnlyNumber(target.value)) {
            target.value = this.removeInvalidCardCharacters(target.value);
            return;
        }
    }

    public pinInputFilled(event: KeyboardEvent, index: number) {
        const target = <HTMLInputElement>event.target;
        if (target.value.length == 2) {
            target.value = target.value[0];
            return;
        }

        if (!this.isValidPinDigit(target.value)) {
            this.pins[index] = null;
            target.value = null;
            this.check[index] = false;
        }

        const nextPinInputHtml = this.fetchHtmlPinInput(index + 1);
        if (!!nextPinInputHtml && this.pins[index] != null) nextPinInputHtml.focus();
    }

    public pinInputUpdated(event: KeyboardEvent, index: number): void {
        const updatedPin = this.convertArrayToString(this.pins);
        this.cardLoginRequest.cardPIN = updatedPin;
        const isFormValid = this.isFormValid(this.card, updatedPin);
        this.isValidCard = isFormValid;
        this.isValidPin = isFormValid;
        this.isValid.emit(isFormValid);
    }

    public continue(): void {
        this.invokeCardLogin();
    }

    private invokeCardLogin(): void {
        //let dummyResponse: ICardLoginResponse = {
        //    applicationId: this.cardLoginRequest.applicationId,
        //    userProfile: {
        //        cardNumber: '1234567890',
        //        customerCode: '100001',
        //    }
        //};
        //this.isLoading = true;
        //this.cardVerified.emit(dummyResponse);

        
        this.isLoading = true;
        UserAuth.cardInitUser(this.cardLoginRequest)
            .pipe(
                finalize(() => (this.isLoading = false))
            )
            .subscribe(
                (result) => this.cardVerified.emit(result), 
                (error) => this.handleCardLoginError(error, SourceApis.Identity)
            );
        
    }

    private handleCardLoginError(error: IErrorDescription, sourceApi: SourceApis): void {
        if (error.code == '1001' && !this.isNewErrorModal) {
            this.setDynatraceEvent('IB_PR_Wrong_CardDetails');
            this._invalidCredentials.next(true);
            const modalRef = this.modalService.open(ErrorMessageModalComponent, {
                windowClass: 'modal-xs',
            });
            (modalRef.componentInstance as ErrorMessageModalComponent).wrongCardPin = true;
        } else ErrorHandler.newError(error);
    }


    // Helper methods from card-info
    private removeInvalidCardCharacters(value: string): string {
        const newValue = value.replace(/[^\d]/g, '');
        return newValue;
    }

    private fetchHtmlPinInput(index: number): HTMLInputElement {
        if (index < 0 || index >= this.pins.length) return;

        var elemContainer = <NodeList>(
            this.elementRef.nativeElement.querySelectorAll('.small-number-container')
        );

        var item = elemContainer.item(index) as HTMLInputElement;
        return item.firstChild as HTMLInputElement;
    }

    private isValidPinDigit(value: string): boolean {
        const valueAsNumber = Number.parseInt(value);
        if (isNaN(valueAsNumber) || valueAsNumber < 0 || valueAsNumber > 9) {
            return false;
        } else {
            return true;
        }
    }

    private convertArrayToString(numbers: number[]): string {
        var result = '';
        for (let i = 0; i < numbers.length; i++) {
            var slot = numbers[i];
            if (slot != null) {
                result += slot.toString();
            }
        }
        return result;
    }

    private isFormValid(cardNumber: string, cardPin: string): boolean {
        return this.isValidCardNumner(cardNumber) && this.isPinValid(cardPin);
    }

    private isPinValid(pin: string): boolean {
        var isValid = this.validCardPINRegex.test(pin);
        return isValid;
    }

    private isValidCardNumner(card: string): boolean {
        var isValid = this.validCardNumberRegex.test(card);
        return isValid;
    }

    private isValidCardOnlyNumber(card: string): boolean {
        var isValid = this.validOnlyNumberRegex.test(card);
        return isValid;
    }
}